import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { partyApi } from '../../services/api.party';
import config from '../../config/environment';
import './AddCandidate.css';


const AddCandidate = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [constituencies, setConstituencies] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [customSymbolFile, setCustomSymbolFile] = useState(null);
  const [customSymbolPreview, setCustomSymbolPreview] = useState('');
  // ✅ NEW: Affidavit PDF file state
  const [affidavitFile, setAffidavitFile] = useState(null);
  const [affidavitPreview, setAffidavitPreview] = useState('');
  
  const [formData, setFormData] = useState({
    constituencyCode: '',
    candidateName: '',
    partyShortName: '',
    partyName: '',
    age: '',
    education: '',
    profession: '',
    address: '',
    backgroundSourceUrl: '',
    assets: '',
    liabilities: '',
    criminalCases: '0',
    email: '',
    mobile: '',
    // ✅ NEW: Affidavit PDF URL field
    affidavitPdfUrl: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);


  useEffect(() => {
    loadConstituencies();
    loadParties();
  }, []);


  const loadConstituencies = async () => {
    try {
      const data = await api.getConstituencies();
      setConstituencies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading constituencies:', error);
    }
  };


  const loadParties = async () => {
    try {
      const data = await partyApi.list({ pageSize: 200 });
      setParties(data.items || []);
    } catch (error) {
      console.error('Error loading parties:', error);
      setParties([]);
    }
  };


  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };


  const handlePartyChange = (e) => {
    const shortName = e.target.value;
    const party = parties.find(p => p.shortName === shortName);
    
    setSelectedParty(party || null);
    setCustomSymbolFile(null);
    setCustomSymbolPreview('');
    
    if (party) {
      setFormData(prev => ({
        ...prev,
        partyShortName: party.shortName || '',
        partyName: party.name || ''
      }));
    } else if (shortName === 'IND') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'IND',
        partyName: 'Independent'
      }));
    } else if (shortName === 'OTHER') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'OTHER',
        partyName: 'Other'
      }));
    } else if (shortName === 'NOTA') {
      setFormData(prev => ({
        ...prev,
        partyShortName: 'NOTA',
        partyName: 'None of the Above'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        partyShortName: '',
        partyName: ''
      }));
    }
  };


  const handleCustomSymbolChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;


    try {
      if (file.size > 2 * 1024 * 1024) {
        throw new Error('Symbol size must be less than 2MB');
      }


      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PNG, JPG, JPEG, GIF, SVG allowed');
      }


      const previewUrl = URL.createObjectURL(file);
      setCustomSymbolPreview(previewUrl);
      setCustomSymbolFile(file);
      
      console.log('✅ Custom symbol file selected:', file.name);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // ✅ NEW: Handle Affidavit PDF Upload
  const handleAffidavitChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('PDF size must be less than 10MB');
      }

      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      setAffidavitFile(file);
      setAffidavitPreview(file.name);
      
      console.log('✅ Affidavit PDF file selected:', file.name);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // ✅ NEW: Remove Affidavit PDF
  const removeAffidavit = () => {
    setAffidavitFile(null);
    setAffidavitPreview('');
  };


  const handleChange = (e) => {
    const { name, value } = e.target;


    if (name === 'mobile') {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({ ...prev, mobile: onlyDigits }));
      return;
    }


    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setMessage({
          type: 'error',
          text: 'Please select a valid image file (JPG, PNG, or GIF)'
        });
        return;
      }


      if (file.size > 5 * 1024 * 1024) {
        setMessage({
          type: 'error',
          text: 'Image size must be less than 5MB'
        });
        return;
      }


      setPhotoFile(file);


      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
      
      console.log('✅ Photo file selected:', file.name);
    }
  };


  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };


  const validateEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };


  const validateMobile = (mobile) => {
    if (!mobile) return true;
    return /^\d{10,15}$/.test(mobile);
  };


  const uploadCustomSymbol = async () => {
    if (!customSymbolFile) return '';
    
    try {
      console.log('🎨 Uploading custom symbol...');
      const result = await partyApi.uploadSymbol(customSymbolFile);
      console.log('✅ Symbol uploaded successfully:', result.url);
      return result.url;
    } catch (error) {
      console.error('❌ Symbol upload error:', error);
      throw error;
    }
  };

  // ✅ NEW: Upload Affidavit PDF
  const uploadAffidavit = async () => {
    if (!affidavitFile) return '';
    
    try {
      console.log('📄 Uploading affidavit PDF...');
      const result = await api.uploadAffidavitPdf(affidavitFile);
      console.log('✅ Affidavit PDF uploaded successfully:', result.url);
      return result.url;
    } catch (error) {
      console.error('❌ Affidavit PDF upload error:', error);
      throw error;
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);


    if (!validateEmail(formData.email)) {
      setLoading(false);
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    if (!validateMobile(formData.mobile)) {
      setLoading(false);
      setMessage({ type: 'error', text: 'Please enter a valid mobile number (10-15 digits)' });
      return;
    }


    try {
      let photoUrl = null;
      let customSymbolUrl = '';
      let affidavitPdfUrl = ''; // ✅ NEW

      // ✅ Upload candidate photo first
      if (photoFile) {
        console.log('📸 Uploading candidate photo...');
        const photoResult = await api.uploadCandidatePhoto(photoFile);
        photoUrl = photoResult.url || photoResult.photoUrl;
        console.log('✅ Photo uploaded:', photoUrl);
      }


      // ✅ Check if party needs custom symbol (IND, OTHER, NOTA)
      const needsCustomSymbol = 
        formData.partyShortName === 'IND' || 
        formData.partyShortName === 'OTHER' ||
        formData.partyShortName === 'NOTA';


      // ✅ Upload custom symbol if needed and file is selected
      if (customSymbolFile && needsCustomSymbol) {
        customSymbolUrl = await uploadCustomSymbol();
      }

      // ✅ NEW: Upload affidavit PDF if provided
      if (affidavitFile) {
        console.log('📄 Uploading affidavit PDF...');
        affidavitPdfUrl = await uploadAffidavit();
        console.log('✅ Affidavit PDF uploaded:', affidavitPdfUrl);
      }


      const candidateData = {
        ...formData,
        photoUrl,
        customSymbol: customSymbolUrl,
        // ✅ NEW: Include affidavit PDF URL
        affidavitPdfUrl
      };


      console.log('📤 Submitting candidate data:', candidateData);


      await api.addCandidate(candidateData);


      setMessage({
        type: 'success',
        text: t('candidate.successMessage') || 'Candidate added successfully!'
      });


      // Reset form
      setFormData({
        constituencyCode: '',
        candidateName: '',
        partyShortName: '',
        partyName: '',
        age: '',
        education: '',
        profession: '',
        address: '',
        backgroundSourceUrl: '',
        assets: '',
        liabilities: '',
        criminalCases: '0',
        email: '',
        mobile: '',
        affidavitPdfUrl: ''
      });
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedParty(null);
      setCustomSymbolFile(null);
      setCustomSymbolPreview('');
      setAffidavitFile(null);
      setAffidavitPreview('');
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('❌ Submit error:', error);
      setMessage({
        type: 'error',
        text: error?.message || t('candidate.errorMessage') || 'Failed to add candidate'
      });
    } finally {
      setLoading(false);
    }
  };


  const getPartySymbol = () => {
    if (customSymbolPreview) return customSymbolPreview;
    if (selectedParty?.symbolUrl) return getFullImageUrl(selectedParty.symbolUrl);
    return '';
  };


  return (
    <div className="add-candidate-container">
      <div className="form-header">
        <h2>
          <i className="fas fa-user-plus"></i>
          {t('candidate.title') || 'Add Candidate'}
        </h2>
        <p>{t('candidate.subtitle') || 'Add a new candidate to the election database'}</p>
      </div>


      {message && (
        <div className={`alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
          <button onClick={() => setMessage(null)} className="alert-close">×</button>
        </div>
      )}


      <form onSubmit={handleSubmit} className="candidate-form">
        {/* Photo Upload Section */}
        <div className="form-section">
          <h3><i className="fas fa-camera"></i> {t('candidate.photoSection') || 'Candidate Photo'}</h3>


          <div className="photo-upload-container">
            {photoPreview ? (
              <div className="photo-preview-wrapper">
                <img src={photoPreview} alt="Preview" className="photo-preview" />
                <button
                  type="button"
                  className="remove-photo-btn"
                  onClick={removePhoto}
                  title="Remove photo"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ) : (
              <div className="photo-upload-box">
                <input
                  type="file"
                  id="photoUpload"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="photoUpload" className="photo-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>{t('candidate.uploadPhoto') || 'Click to upload photo'}</span>
                  <small>{t('candidate.photoHint') || 'JPG, PNG or GIF (Max 5MB)'}</small>
                </label>
              </div>
            )}
          </div>
        </div>


        {/* Constituency Details */}
        <div className="form-section">
          <h3><i className="fas fa-landmark"></i> {t('candidate.constituencyDetails') || 'Constituency Details'}</h3>


          <div className="form-group">
            <label htmlFor="constituencyCode">
              <i className="fas fa-map-pin"></i>
              {t('candidate.constituency') || 'Constituency'} *
            </label>
            <select
              id="constituencyCode"
              name="constituencyCode"
              value={formData.constituencyCode}
              onChange={handleChange}
              required
            >
              <option value="">{t('candidate.selectConstituency') || 'Select Constituency'}</option>
              {constituencies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>


        {/* Personal Details */}
        <div className="form-section">
          <h3><i className="fas fa-user"></i> {t('candidate.personalDetails') || 'Personal Details'}</h3>


          <div className="form-row">
            <div className="form-group">
              <label htmlFor="candidateName">
                <i className="fas fa-id-card"></i>
                {t('candidate.candidateName') || 'Candidate Name'} *
              </label>
              <input
                id="candidateName"
                type="text"
                name="candidateName"
                value={formData.candidateName}
                onChange={handleChange}
                placeholder={t('candidate.fullNamePlaceholder') || 'Enter full name'}
                required
              />
            </div>


            <div className="form-group">
              <label htmlFor="age">
                <i className="fas fa-birthday-cake"></i>
                {t('candidate.age') || 'Age'}
              </label>
              <input
                id="age"
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                placeholder="45"
                min="25"
                max="100"
              />
            </div>
          </div>


          <div className="form-row">
            <div className="form-group">
              <label htmlFor="education">
                <i className="fas fa-graduation-cap"></i>
                {t('candidate.education') || 'Education'}
              </label>
              <input
                id="education"
                type="text"
                name="education"
                value={formData.education}
                onChange={handleChange}
                placeholder={t('candidate.educationPlaceholder') || 'e.g., Graduate, Post Graduate'}
              />
            </div>


            <div className="form-group">
              <label htmlFor="profession">
                <i className="fas fa-briefcase"></i>
                {t('candidate.profession') || 'Profession'}
              </label>
              <input
                id="profession"
                type="text"
                name="profession"
                value={formData.profession}
                onChange={handleChange}
                placeholder={t('candidate.professionPlaceholder') || 'e.g., Politician, Lawyer'}
              />
            </div>
          </div>


          {/* Contact Details */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">
                <i className="fas fa-envelope"></i>
                {t('candidate.email') || 'Email'}
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="candidate@example.com"
              />
            </div>


            <div className="form-group">
              <label htmlFor="mobile">
                <i className="fas fa-phone"></i>
                {t('candidate.mobile') || 'Mobile'}
              </label>
              <input
                id="mobile"
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="10-15 digits"
                inputMode="numeric"
                pattern="\d*"
              />
              <small className="field-hint">
                {t('candidate.mobileHint') || 'Only digits, 10-15 length'}
              </small>
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="address">
              <i className="fas fa-home"></i>
              {t('candidate.address') || 'Address'}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('candidate.addressPlaceholder') || 'Enter full address'}
              rows="3"
            />
          </div>
        </div>


        {/* Party Information with Auto-fill */}
        <div className="form-section party-section">
          <h3><i className="fas fa-flag"></i> {t('candidate.partyDetails') || 'Party Details'}</h3>
          
          <div className="party-selector-wrapper">
            <div className="form-group party-dropdown">
              <label htmlFor="partySelect">
                <i className="fas fa-flag"></i>
                {t('candidate.selectParty') || 'Select Party'} *
              </label>
              <select
                id="partySelect"
                value={selectedParty?.shortName || formData.partyShortName}
                onChange={handlePartyChange}
                required
              >
                <option value="">Choose a party...</option>
                <optgroup label="Major Parties">
                  {parties.filter(p => p.candidatesCount > 0).map(party => (
                    <option key={party.id} value={party.shortName}>
                      {party.shortName} - {party.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Other Parties">
                  {parties.filter(p => p.candidatesCount === 0).map(party => (
                    <option key={party.id} value={party.shortName}>
                      {party.shortName} - {party.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Special">
                  <option value="IND">IND - Independent</option>
                  <option value="NOTA">NOTA - None of the Above</option>
                  <option value="OTHER">OTHER - Enter Manually</option>
                </optgroup>
              </select>
            </div>


            {/* Party Symbol Display */}
            {(selectedParty || formData.partyShortName) && (
              <div className="party-symbol-display">
                <div className="symbol-preview-box">
                  {getPartySymbol() ? (
                    <img src={getPartySymbol()} alt="Party Symbol" />
                  ) : (
                    <div className="no-symbol">
                      <i className="fas fa-image"></i>
                      <p>No Symbol</p>
                    </div>
                  )}
                </div>
                <div className="symbol-info">
                  <p><strong>{formData.partyShortName}</strong></p>
                  <p className="party-full-name">{formData.partyName}</p>
                </div>
              </div>
            )}
          </div>


          {/* Manual Party Entry for OTHER */}
          {formData.partyShortName === 'OTHER' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="partyShortName">
                  <i className="fas fa-tag"></i>
                  Party Short Name *
                </label>
                <input
                  id="partyShortName"
                  type="text"
                  name="partyShortName"
                  value={formData.partyShortName}
                  onChange={handleChange}
                  placeholder="BJP"
                  required
                  maxLength={10}
                />
              </div>


              <div className="form-group">
                <label htmlFor="partyName">
                  <i className="fas fa-flag"></i>
                  Party Full Name *
                </label>
                <input
                  id="partyName"
                  type="text"
                  name="partyName"
                  value={formData.partyName}
                  onChange={handleChange}
                  placeholder="Bharatiya Janata Party"
                  required
                />
              </div>
            </div>
          )}


          {/* Custom Symbol Upload for Independent/Other/NOTA */}
          {(formData.partyShortName === 'IND' || 
            formData.partyShortName === 'OTHER' || 
            formData.partyShortName === 'NOTA') && (
            <div className="form-group custom-symbol-upload">
              <label>
                <i className="fas fa-image"></i>
                Upload Custom Symbol {formData.partyShortName === 'IND' ? '(Optional)' : '(Required)'}
              </label>
              <div className="upload-box">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCustomSymbolChange}
                  id="customSymbol"
                  style={{ display: 'none' }}
                />
                <label htmlFor="customSymbol" className="upload-label">
                  <i className="fas fa-upload"></i>
                  <span>Choose Symbol Image</span>
                </label>
              </div>
              <small>PNG, JPG, JPEG, GIF, SVG up to 2MB</small>
              
              {customSymbolPreview && (
                <div className="symbol-preview-small" style={{marginTop: '12px', padding: '10px', background: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
                  <img 
                    src={customSymbolPreview} 
                    alt="Symbol Preview" 
                    style={{
                      maxWidth: '100px', 
                      maxHeight: '100px', 
                      objectFit: 'contain',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '5px',
                      background: 'white'
                    }} 
                  />
                  <p style={{margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280'}}>
                    Symbol Selected ✅
                  </p>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Financial Details */}
        <div className="form-section">
          <h3><i className="fas fa-money-bill-wave"></i> {t('candidate.financialDetails') || 'Financial Details'}</h3>


          <div className="form-row">
            <div className="form-group">
              <label htmlFor="assets">
                <i className="fas fa-coins"></i>
                {t('candidate.totalAssets') || 'Total Assets'} (₹)
              </label>
              <input
                id="assets"
                type="number"
                name="assets"
                value={formData.assets}
                onChange={handleChange}
                placeholder="10000000"
                min="0"
              />
            </div>


            <div className="form-group">
              <label htmlFor="liabilities">
                <i className="fas fa-file-invoice-dollar"></i>
                {t('candidate.totalLiabilities') || 'Total Liabilities'} (₹)
              </label>
              <input
                id="liabilities"
                type="number"
                name="liabilities"
                value={formData.liabilities}
                onChange={handleChange}
                placeholder="500000"
                min="0"
              />
            </div>
          </div>


          <div className="form-group">
            <label htmlFor="criminalCases">
              <i className="fas fa-gavel"></i>
              {t('candidate.criminalCases') || 'Criminal Cases'}
            </label>
            <input
              id="criminalCases"
              type="number"
              name="criminalCases"
              value={formData.criminalCases}
              onChange={handleChange}
              placeholder="0"
              min="0"
            />
          </div>
        </div>


        {/* ✅ NEW: Affidavit PDF Section */}
        <div className="form-section">
          <h3><i className="fas fa-file-pdf"></i> Affidavit PDF</h3>
          
          <div className="affidavit-upload-container">
            {affidavitPreview ? (
              <div className="affidavit-preview-wrapper">
                <div className="affidavit-preview">
                  <i className="fas fa-file-pdf"></i>
                  <span>{affidavitPreview}</span>
                </div>
                <button
                  type="button"
                  className="remove-affidavit-btn"
                  onClick={removeAffidavit}
                  title="Remove affidavit"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ) : (
              <div className="affidavit-upload-box">
                <input
                  type="file"
                  id="affidavitUpload"
                  accept="application/pdf"
                  onChange={handleAffidavitChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="affidavitUpload" className="affidavit-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Click to upload affidavit PDF</span>
                  <small>PDF only (Max 10MB)</small>
                </label>
              </div>
            )}
          </div>
        </div>


        {/* Additional Information */}
        <div className="form-section">
          <h3><i className="fas fa-info-circle"></i> {t('candidate.additionalInfo') || 'Additional Information'}</h3>


          <div className="form-group">
            <label htmlFor="backgroundSourceUrl">
              <i className="fas fa-link"></i>
              {t('candidate.backgroundSourceUrl') || 'Background Source URL'}
            </label>
            <input
              id="backgroundSourceUrl"
              type="url"
              name="backgroundSourceUrl"
              value={formData.backgroundSourceUrl}
              onChange={handleChange}
              placeholder="https://myneta.info/candidate123"
            />
            <small className="field-hint">
              {t('candidate.backgroundSourceUrlHint') || 'Enter the source URL for candidate background information'}
            </small>
          </div>
        </div>


        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? (
              <>
                <div className="spinner"></div>
                {t('candidate.adding') || 'Adding...'}
              </>
            ) : (
              <>
                <i className="fas fa-plus-circle"></i>
                {t('candidate.addButton') || 'Add Candidate'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData({
                constituencyCode: '',
                candidateName: '',
                partyShortName: '',
                partyName: '',
                age: '',
                education: '',
                profession: '',
                address: '',
                backgroundSourceUrl: '',
                assets: '',
                liabilities: '',
                criminalCases: '0',
                email: '',
                mobile: '',
                affidavitPdfUrl: ''
              });
              removePhoto();
              setSelectedParty(null);
              setCustomSymbolFile(null);
              setCustomSymbolPreview('');
              removeAffidavit();
            }}
            className="btn btn-secondary"
            disabled={loading}
          >
            <i className="fas fa-redo"></i>
            {t('candidate.resetButton') || 'Reset Form'}
          </button>
        </div>
      </form>
    </div>
  );
};


export default AddCandidate;
